import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  SimilaridadeState,
  SimilaridadeFilters,
  MapData,
  EnsembleData,
  IndiceClimatico,
} from '../../types/Meteorology/indicesClimaticos';

const initialState: SimilaridadeState = {
  filters: {
    variavel: '',
    anos: [],
    meses: [],
  },
  maps: {
    data: [],
    loading: false,
    generated: false,
  },
  ensemble: {
    data: null,
    modalOpen: false,
    loading: false,
  },
  actions: {
    canGenerateMaps: false,
    canGenerateEnsemble: false,
  },
  tableData: [],
  selectedRows: [],
};

export const similaridadeSlice = createSlice({
  name: 'similaridade',
  initialState,
  reducers: {
    // Reset state
    reset: (state) => {
      Object.assign(state, initialState);
    },

    // Filter actions
    setVariavel: (state, action: PayloadAction<string>) => {
      state.filters.variavel = action.payload;
      // Atualiza estado do botão baseado em variável E mês
      state.actions.canGenerateMaps = action.payload !== '' && state.filters.meses.length > 0;
      // Reset maps when variable changes
      state.maps.data = [];
      state.maps.generated = false;
      state.actions.canGenerateEnsemble = false;
      state.selectedRows = [];
    },

    setAnos: (state, action: PayloadAction<number[]>) => {
      state.filters.anos = action.payload;
    },

    setMeses: (state, action: PayloadAction<number[]>) => {
      state.filters.meses = action.payload;
      // Atualiza estado do botão baseado em variável E mês
      state.actions.canGenerateMaps = state.filters.variavel !== '' && action.payload.length > 0;
    },

    updateFilters: (state, action: PayloadAction<Partial<SimilaridadeFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      // Update actions based on filters
      state.actions.canGenerateMaps = state.filters.variavel !== '' && state.filters.meses.length > 0;
    },

    // Maps actions
    setMapsLoading: (state, action: PayloadAction<boolean>) => {
      state.maps.loading = action.payload;
    },

    setMapsData: (state, action: PayloadAction<MapData[]>) => {
      state.maps.data = action.payload;
      state.maps.generated = action.payload.length > 0;
      state.maps.loading = false;
      state.actions.canGenerateEnsemble = action.payload.length > 0;

      // Update selected rows based on generated maps
      state.selectedRows = action.payload.map((map) => state.tableData.findIndex(
        (row) => row.ano === map.ano && row.mes === map.mes,
      )).filter((index) => index !== -1);
    },

    addMapData: (state, action: PayloadAction<MapData>) => {
      const existingIndex = state.maps.data.findIndex(
        (map) => map.ano === action.payload.ano && map.mes === action.payload.mes,
      );

      if (existingIndex === -1) {
        state.maps.data.push(action.payload);

        // Add corresponding row to selectedRows
        const rowIndex = state.tableData.findIndex(
          (row) => row.ano === action.payload.ano && row.mes === action.payload.mes,
        );
        if (rowIndex !== -1 && !state.selectedRows.includes(rowIndex)) {
          state.selectedRows.push(rowIndex);
        }
      }

      state.maps.generated = state.maps.data.length > 0;
      state.actions.canGenerateEnsemble = state.maps.data.length > 0;
    },

    removeMapData: (state, action: PayloadAction<{ ano: number; mes: number }>) => {
      const { ano, mes } = action.payload;

      // Remove from maps data
      state.maps.data = state.maps.data.filter(
        (map) => !(map.ano === ano && map.mes === mes),
      );

      // Remove from selected rows (bidirectional sync)
      const rowIndex = state.tableData.findIndex(
        (row) => row.ano === ano && row.mes === mes,
      );
      if (rowIndex !== -1) {
        state.selectedRows = state.selectedRows.filter((index) => index !== rowIndex);
      }

      state.maps.generated = state.maps.data.length > 0;
      state.actions.canGenerateEnsemble = state.maps.data.length > 0;
    },

    clearMaps: (state) => {
      state.maps.data = [];
      state.maps.generated = false;
      state.actions.canGenerateEnsemble = false;
      state.selectedRows = [];
    },

    // Ensemble actions
    setEnsembleLoading: (state, action: PayloadAction<boolean>) => {
      state.ensemble.loading = action.payload;
    },

    setEnsembleData: (state, action: PayloadAction<EnsembleData | null>) => {
      state.ensemble.data = action.payload;
      state.ensemble.loading = false;
      if (action.payload) {
        state.ensemble.modalOpen = true;
      }
    },

    setEnsembleModalOpen: (state, action: PayloadAction<boolean>) => {
      state.ensemble.modalOpen = action.payload;
    },

    clearEnsemble: (state) => {
      state.ensemble.data = null;
      state.ensemble.modalOpen = false;
      state.ensemble.loading = false;
    },

    // Table data actions
    setTableData: (state, action: PayloadAction<IndiceClimatico[]>) => {
      state.tableData = action.payload;
    },

    // Selected rows actions (for bidirectional sync)
    setSelectedRows: (state, action: PayloadAction<number[]>) => {
      state.selectedRows = action.payload;

      // This action should trigger map generation
      // The actual API call will be handled by the component/hook
    },

    addSelectedRow: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (!state.selectedRows.includes(index) && state.tableData[index]) {
        state.selectedRows.push(index);
      }
    },

    removeSelectedRow: (state, action: PayloadAction<number>) => {
      state.selectedRows = state.selectedRows.filter((index) => index !== action.payload);

      // Remove corresponding map if exists
      const rowData = state.tableData[action.payload];
      if (rowData) {
        state.maps.data = state.maps.data.filter(
          (map) => !(map.ano === rowData.ano && map.mes === rowData.mes),
        );
      }

      state.maps.generated = state.maps.data.length > 0;
      state.actions.canGenerateEnsemble = state.maps.data.length > 0;
    },

    // Action button state management
    updateActionStates: (state) => {
      // Botão Gerar Mapas habilitado quando tem variável E pelo menos um mês selecionado
      state.actions.canGenerateMaps = state.filters.variavel !== '' && state.filters.meses.length > 0;
      state.actions.canGenerateEnsemble = state.maps.generated && state.maps.data.length > 0;
    },
  },
});

// Selectors
export const selectSimilaridadeFilters = (state: any) => state.similaridade.filters;
export const selectSimilaridadeVariavel = (state: any) => state.similaridade.filters.variavel;
export const selectSimilaridadeAnos = (state: any) => state.similaridade.filters.anos;
export const selectSimilaridadeMeses = (state: any) => state.similaridade.filters.meses;

export const selectSimilaridadeMaps = (state: any) => state.similaridade.maps;
export const selectSimilaridadeMapsData = (state: any) => state.similaridade.maps.data;
export const selectSimilaridadeMapsLoading = (state: any) => state.similaridade.maps.loading;
export const selectSimilaridadeMapsGenerated = (state: any) => state.similaridade.maps.generated;

export const selectSimilaridadeEnsemble = (state: any) => state.similaridade.ensemble;
export const selectSimilaridadeEnsembleData = (state: any) => state.similaridade.ensemble.data;
export const selectSimilaridadeEnsembleModalOpen = (state: any) => (
  state.similaridade.ensemble.modalOpen
);
export const selectSimilaridadeEnsembleLoading = (state: any) => (
  state.similaridade.ensemble.loading
);

export const selectSimilaridadeActions = (state: any) => state.similaridade.actions;
export const selectSimilaridadeCanGenerateMaps = (state: any) => (
  state.similaridade.actions.canGenerateMaps
);
export const selectSimilaridadeCanGenerateEnsemble = (state: any) => (
  state.similaridade.actions.canGenerateEnsemble
);

export const selectSimilaridadeTableData = (state: any) => state.similaridade.tableData;
export const selectSimilaridadeSelectedRows = (state: any) => state.similaridade.selectedRows;

// Export actions
export const {
  reset,
  setVariavel,
  setAnos,
  setMeses,
  updateFilters,
  setMapsLoading,
  setMapsData,
  addMapData,
  removeMapData,
  clearMaps,
  setEnsembleLoading,
  setEnsembleData,
  setEnsembleModalOpen,
  clearEnsemble,
  setTableData,
  setSelectedRows,
  addSelectedRow,
  removeSelectedRow,
  updateActionStates,
} = similaridadeSlice.actions;

export default similaridadeSlice.reducer;
